import { useState, useCallback, useRef } from 'react';
import { clubsService } from '@/features/clubs/services/clubsService';
import type { GalleryItemResponse } from '@/features/clubs/types/types';

const ITEMS_PER_PAGE = 50;

interface UseInfiniteGalleryDataReturn {
    galleryItems: GalleryItemResponse[];
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    hasMore: boolean;
    loadMore: () => Promise<void>;
    refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage paginated gallery data with infinite scrolling
 * @param handle - The business handle to fetch gallery for
 * @param autoFetch - Whether to automatically fetch data on mount (default: true)
 * @returns Gallery data, loading states, pagination controls
 */
export const useInfiniteGalleryData = (
    handle: string,
    autoFetch: boolean = true
): UseInfiniteGalleryDataReturn => {
    const [galleryItems, setGalleryItems] = useState<GalleryItemResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const offsetRef = useRef(0);
    const initialFetchDone = useRef(false);

    const fetchGalleryData = useCallback(async (isInitial: boolean = false) => {
        if (!handle) return;

        if (isInitial) {
            setLoading(true);
            setGalleryItems([]);
            offsetRef.current = 0;
            setHasMore(true);
        } else {
            setLoadingMore(true);
        }
        setError(null);

        try {
            const data = await clubsService.getGalleryByBusinessHandle(
                handle,
                ITEMS_PER_PAGE,
                offsetRef.current
            );

            if (data.length < ITEMS_PER_PAGE) {
                setHasMore(false);
            }

            if (isInitial) {
                setGalleryItems(data);
            } else {
                setGalleryItems(prev => [...prev, ...data]);
            }

            offsetRef.current += data.length;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gallery';
            setError(errorMessage);
            console.error('Error fetching gallery:', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [handle]);

    // Auto-fetch on mount if handle is provided
    if (handle && autoFetch && !initialFetchDone.current && !loading) {
        initialFetchDone.current = true;
        fetchGalleryData(true);
    }

    const loadMore = useCallback(async () => {
        if (!loadingMore && !loading && hasMore) {
            await fetchGalleryData(false);
        }
    }, [loadingMore, loading, hasMore, fetchGalleryData]);

    const refetch = useCallback(async () => {
        initialFetchDone.current = false;
        await fetchGalleryData(true);
    }, [fetchGalleryData]);

    return {
        galleryItems,
        loading,
        loadingMore,
        error,
        hasMore,
        loadMore,
        refetch,
    };
};
